import type { TenantVerticalLiteral } from "@eduviaos/shared";
import type { FirstRunStepDescriptor } from "@/components/FirstRunShell";
import { getVerticalProfile } from "@/lib/verticalProfile";

/**
 * Canonical step list for the IA first-run wizard. C1c adds the
 * import_students step on top of C1a's welcome + C1b's invite_staff.
 * Later phases (C1d — import-staff, C1e — academic-year, …) append
 * their own keys here once they land — the `FirstRunShell` renders
 * dots for everything declared, so adding a key is the only change
 * needed.
 *
 * Lives in its own module (separate from `FirstRunShell.tsx`) so the
 * Vite/SWC fast-refresh boundary stays component-only — exporting both
 * components and constants from a single file flips the
 * `react-refresh/only-export-components` warning.
 */
export const FIRST_RUN_STEPS: FirstRunStepDescriptor[] = [
  { key: "welcome", label: "Welcome" },
  { key: "branding", label: "Branding" },
  { key: "invite_staff", label: "Invite staff" },
  { key: "import_students", label: "Import students" },
];

/**
 * SC Foundation (F1) — vertical-aware step labels. Study centres have no
 * "staff/students" framing, so their rail reads "Invite team" / "Import
 * learners" (derived from the vertical profile's staff + person nouns).
 * School and coaching centres keep the canonical `FIRST_RUN_STEPS`
 * byte-for-byte, so their wizard is untouched.
 *
 * Phase F2b — the "branding" step (logo + colours the IA applies to its
 * own admin portal AND its learners' portal) sits second for EVERY
 * vertical: branding matters as much to a school as a study centre, and
 * keeping the key set identical across verticals keeps the router +
 * step-count derivation simple.
 */
export function buildFirstRunSteps(vertical: TenantVerticalLiteral): FirstRunStepDescriptor[] {
  if (vertical !== "study_centre") {
    return FIRST_RUN_STEPS;
  }
  const profile = getVerticalProfile(vertical);
  return [
    { key: "welcome", label: "Welcome" },
    { key: "branding", label: "Branding" },
    { key: "invite_staff", label: `Invite ${profile.staffNoun.many.toLowerCase()}` },
    { key: "import_students", label: `Import ${profile.personNoun.many.toLowerCase()}` },
    { key: "comms", label: "Communications" },
    { key: "go_live", label: "Go live" },
  ];
}

/**
 * Human "Step N of M" eyebrow derived from the (vertical-aware) step
 * list so adding / reordering steps never desyncs the hardcoded counts
 * that used to live in each wizard page. Falls back to step 1 when the
 * key isn't found (defensive — should never happen in practice).
 */
export function firstRunEyebrow(vertical: TenantVerticalLiteral, stepKey: string): string {
  const steps = buildFirstRunSteps(vertical);
  const idx = steps.findIndex((s) => s.key === stepKey);
  const position = idx === -1 ? 1 : idx + 1;
  return `Step ${position} of ${steps.length}`;
}
